import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { t as translate, MARKETS } from "@/i18n";
import { readConsent, applyConsent, CONSENT_OPEN_EVENT } from "@/lib/consent";

// GDPR cookie consent — YANRE design language (carbon, signal red, sharp geometry).
// Mounted ONCE globally in App.js. Locale is derived from the route so the banner
// follows the site's multi-language architecture without touching LocaleProvider wiring.
// RTL: inherits <html dir> set by LocaleContext on Arabic routes.

// Language locales + Africa Wave 1 country-market prefixes (e.g. /ma/…).
const localeFromPath = (pathname) => {
  const m = pathname.match(/^\/(pt|es|fr|de|ar|br|it|za|ma|eg|dz|mu|ke|ao|bw)(\/|$)/);
  return m ? m[1] : "en";
};

const policyPath = (locale) => (locale === "en" ? "/cookie-policy" : `/${locale}/cookie-policy`);

const Toggle = ({ on, onChange, testId, label, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!on)}
    data-testid={testId}
    className={`relative h-6 w-11 shrink-0 border transition-colors duration-200 ${
      on ? "border-signal bg-signal" : "border-carbon-500 bg-carbon-800"
    } ${disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
  >
    <span
      className={`absolute top-0.5 start-0.5 h-[18px] w-[18px] bg-white transition-transform duration-200 ${
        on ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

export const CookieConsent = () => {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  // Country markets render their primary language dictionary (ma→fr, ao→pt…).
  const t = (k, f) => translate(locale, k, f, MARKETS[locale]?.lang);

  const [visible, setVisible] = useState(() => !readConsent());
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, advertising: false });

  useEffect(() => {
    const open = () => {
      const saved = readConsent();
      setPrefs(saved ? { analytics: !!saved.analytics, advertising: !!saved.advertising } : { analytics: false, advertising: false });
      setManage(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
  }, []);

  const decide = (choices) => {
    applyConsent(choices);
    setVisible(false);
    setManage(false);
  };

  const policy = policyPath(locale);
  const linkCls =
    "font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50 underline-offset-4 transition-colors hover:text-white hover:underline";

  return (
    <AnimatePresence>
      {visible && !manage && (
        <motion.div
          key="banner"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[100] border-t border-carbon-600 bg-carbon-950/95 backdrop-blur-md"
          role="region"
          aria-label={t("ck.title", "Your privacy choices")}
          data-testid="cookie-consent-banner"
        >
          <div className="container-edge flex flex-col gap-5 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-signal" />
                <p className="overline" data-testid="cookie-consent-title">{t("ck.title", "Your privacy choices")}</p>
              </div>
              <p className="mt-2 text-sm font-light leading-relaxed text-titanium/70" data-testid="cookie-consent-desc">
                {t(
                  "ck.desc",
                  "We use optional analytics and advertising cookies only with your consent. You can change or withdraw your choice at any time."
                )}
              </p>
              <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
                <Link to={policy} className={linkCls} data-testid="cookie-policy-link">
                  {t("ck.policy", "Cookie Policy")}
                </Link>
                <Link to={`${policy}#privacy`} className={linkCls} data-testid="privacy-policy-link">
                  {t("ck.privacy", "Privacy Policy")}
                </Link>
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button type="button" onClick={() => decide({ analytics: true, advertising: true })} className="btn-signal" data-testid="cookie-accept-all-btn">
                {t("ck.acceptAll", "Accept All")}
              </button>
              <button
                type="button"
                onClick={() => decide({ analytics: false, advertising: false })}
                className="btn-ghost border-titanium/30 text-titanium hover:border-signal hover:text-white"
                data-testid="cookie-reject-btn"
              >
                {t("ck.reject", "Reject Non-Essential")}
              </button>
              <button
                type="button"
                onClick={() => setManage(true)}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/60 underline-offset-4 transition-colors hover:text-white hover:underline"
                data-testid="cookie-manage-btn"
              >
                {t("ck.manage", "Manage Preferences")}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {visible && manage && (
        <motion.div
          key="modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-end justify-center bg-carbon-950/80 backdrop-blur-sm sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t("ck.prefsTitle", "Cookie Preferences")}
          data-testid="cookie-consent-modal"
        >
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 32, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg border border-carbon-600 bg-carbon-900"
          >
            <div className="flex items-center justify-between border-b border-carbon-700 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-signal" />
                <p className="overline">{t("ck.prefsTitle", "Cookie Preferences")}</p>
              </div>
              <button
                type="button"
                onClick={() => { setManage(false); if (readConsent()) setVisible(false); }}
                aria-label="Close"
                className="text-titanium/60 transition-colors hover:text-white"
                data-testid="cookie-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 px-6 py-6">
              {/* Necessary — always on, cannot be disabled */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.necessary", "Necessary")}</p>
                  <p className="mt-1 text-sm font-light text-titanium/60">{t("ck.necessaryDesc", "Required for the website to function. Always active.")}</p>
                </div>
                <span className="mt-1 shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-signal" data-testid="cookie-necessary-always-on">
                  {t("ck.alwaysOn", "Always active")}
                </span>
              </div>

              <div className="flex items-start justify-between gap-4 border-t border-carbon-700 pt-5">
                <div>
                  <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.analytics", "Analytics")}</p>
                  <p className="mt-1 text-sm font-light text-titanium/60">{t("ck.analyticsDesc", "Anonymous usage statistics that help us improve this website.")}</p>
                </div>
                <Toggle on={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testId="cookie-analytics-toggle" label={t("ck.analytics", "Analytics")} />
              </div>

              <div className="flex items-start justify-between gap-4 border-t border-carbon-700 pt-5">
                <div>
                  <p className="font-display text-base font-bold uppercase tracking-wide text-white">{t("ck.advertising", "Advertising")}</p>
                  <p className="mt-1 text-sm font-light text-titanium/60">{t("ck.advertisingDesc", "Used to measure campaigns and deliver relevant advertising.")}</p>
                </div>
                <Toggle on={prefs.advertising} onChange={(v) => setPrefs((p) => ({ ...p, advertising: v }))} testId="cookie-advertising-toggle" label={t("ck.advertising", "Advertising")} />
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-carbon-700 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex flex-wrap gap-x-5 gap-y-1">
                <Link to={policy} className={linkCls} data-testid="cookie-policy-link-modal">
                  {t("ck.policy", "Cookie Policy")}
                </Link>
                <Link to={`${policy}#privacy`} className={linkCls} data-testid="privacy-policy-link-modal">
                  {t("ck.privacy", "Privacy Policy")}
                </Link>
              </p>
              <button type="button" onClick={() => decide(prefs)} className="btn-signal shrink-0" data-testid="cookie-save-btn">
                {t("ck.save", "Save Preferences")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
